<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { useTranslation } from 'i18next-vue';
import Modal from '@/client/components/common/modal.vue';
import ToggleSwitch from '@/client/components/common/toggle_switch.vue';
import CategoryMappingEditor from '@/client/components/logged_in/category-mapping-editor.vue';
import { ADD_CATEGORY_VALUE } from '@/client/components/logged_in/category-mapping-constants';
import { useFeedStore } from '@/client/stores/feedStore';
import { useFeedFollows } from '@/client/composables/useFeedFollows';
import FeedService, { type RemoteCalendarPreview, type CategoryEntry, type CategoryMappingEntry } from '@/client/service/feed';
import { isResolvableCalendarUrlName } from '@/common/validation/calendarUrlName';
import {
  InvalidRemoteCalendarIdentifierError,
  RemoteCalendarNotFoundError,
  RemoteDomainUnreachableError,
  ActivityPubNotSupportedError,
  RemoteProfileFetchError,
  SelfFollowError,
  AlreadyFollowingError,
} from '@/common/exceptions/activitypub';
import { InsufficientCalendarPermissionsError } from '@/common/exceptions/calendar';

const { t } = useTranslation('feed');

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'follow-success'): void;
}>();

const feedStore = useFeedStore();
const { followCalendar } = useFeedFollows();
const identifier = ref('');
const autoRepostOriginals = ref(false);
const autoRepostReposts = ref(false);
const preview = ref<RemoteCalendarPreview | null>(null);
const isLookingUp = ref(false);
const lookupError = ref('');
const isFollowing = ref(false);

// Mapping step state
const showMappingStep = ref(false);
const pendingActorId = ref('');
const pendingSourceCategories = ref<CategoryEntry[]>([]);
const localCategories = ref<CategoryEntry[]>([]);
const pendingMappings = ref<CategoryMappingEntry[]>([]);
const isSavingMappings = ref(false);
const mappingStepRef = ref<HTMLElement | null>(null);

// Mirrors ActivityPubService.normalizeIdentifier: `username@domain` for a qualified lookup, or a bare urlName checked with the resolvable (lookup) rule.
const isValidIdentifier = computed(() => {
  const value = identifier.value.trim();
  if (!value) {
    return false;
  }
  if (value.includes('@')) {
    return value.split('@').length === 2;
  }
  return isResolvableCalendarUrlName(value);
});

const canLookup = computed(() => {
  return isValidIdentifier.value && !isLookingUp.value;
});

const canFollow = computed(() => {
  return preview.value !== null && !isFollowing.value;
});

let lookupTimeout: ReturnType<typeof setTimeout> | null = null;

const handleIdentifierInput = () => {
  // Clear previous lookup results
  preview.value = null;
  lookupError.value = '';

  // Debounce lookup
  if (lookupTimeout) {
    clearTimeout(lookupTimeout);
  }

  if (isValidIdentifier.value) {
    lookupTimeout = setTimeout(() => {
      lookupRemoteCalendar();
    }, 500);
  }
};

const lookupRemoteCalendar = async () => {
  if (!isValidIdentifier.value) {
    return;
  }

  isLookingUp.value = true;
  lookupError.value = '';
  preview.value = null;

  try {
    const feedService = new FeedService();
    const result = await feedService.lookupRemoteCalendar(identifier.value.trim());

    // Check for self-follow (local calendar matching selected calendar)
    if (result.calendarId && result.calendarId === feedStore.selectedCalendarId) {
      lookupError.value = t('add_calendar_modal.self_follow_error');
      return;
    }

    preview.value = result;
  }
  catch (error: any) {
    console.error('Error looking up remote calendar:', error);
    // Map exception types to translated error messages
    switch (error.constructor) {
      case InvalidRemoteCalendarIdentifierError:
        lookupError.value = t('errors.InvalidRemoteCalendarIdentifierError');
        break;
      case RemoteCalendarNotFoundError:
        lookupError.value = t('errors.RemoteCalendarNotFoundError');
        break;
      case RemoteDomainUnreachableError:
        lookupError.value = t('errors.RemoteDomainUnreachableError');
        break;
      case ActivityPubNotSupportedError:
        lookupError.value = t('errors.ActivityPubNotSupportedError');
        break;
      case RemoteProfileFetchError:
        lookupError.value = t('errors.RemoteProfileFetchError');
        break;
      default:
        lookupError.value = t('add_calendar_modal.lookup_error');
    }
  }
  finally {
    isLookingUp.value = false;
  }
};

const handleOriginalsToggle = (value: boolean) => {
  autoRepostOriginals.value = value;
  // If turning off originals, also turn off reposts
  if (!value) {
    autoRepostReposts.value = false;
  }
};

const handleRepostsToggle = (value: boolean) => {
  autoRepostReposts.value = value;
};

const handleFollow = async () => {
  if (!canFollow.value) {
    return;
  }

  isFollowing.value = true;

  try {
    const calendarActorId = await followCalendar(identifier.value.trim(), autoRepostOriginals.value, autoRepostReposts.value);
    emit('follow-success');

    if (calendarActorId && feedStore.selectedCalendarId) {
      await loadMappingStep(feedStore.selectedCalendarId, calendarActorId);
    }
    else {
      handleClose();
    }
  }
  catch (error: any) {
    console.error('Error following calendar:', error);
    // Map exception types to translated error messages
    switch (error.constructor) {
      case InvalidRemoteCalendarIdentifierError:
        lookupError.value = t('errors.InvalidRemoteCalendarIdentifierError');
        break;
      case InsufficientCalendarPermissionsError:
        lookupError.value = t('errors.InsufficientCalendarPermissionsError');
        break;
      case SelfFollowError:
        lookupError.value = t('errors.SelfFollowError');
        break;
      case AlreadyFollowingError:
        lookupError.value = t('errors.AlreadyFollowingError');
        break;
      default:
        lookupError.value = t('add_calendar_modal.follow_error');
    }
  }
  finally {
    isFollowing.value = false;
  }
};

/**
 * Load source categories and local categories for the mapping step.
 * Automatically skips the step if there are no source categories.
 */
const loadMappingStep = async (calendarId: string, actorId: string) => {
  try {
    const feedService = new FeedService();
    const [sourceCategories, calendarCategories] = await Promise.all([
      feedService.getSourceCategories(calendarId, actorId),
      feedService.getCalendarCategories(calendarId),
    ]);

    if (sourceCategories.length === 0) {
      // No source categories — skip mapping step and close
      handleClose();
      return;
    }

    pendingActorId.value = actorId;
    pendingSourceCategories.value = sourceCategories;
    localCategories.value = calendarCategories;
    pendingMappings.value = [];
    showMappingStep.value = true;
    await nextTick();
    mappingStepRef.value?.focus();
  }
  catch {
    // If we can't load categories, just close — mapping can be configured later
    handleClose();
  }
};

const saveMappingsAndClose = async () => {
  if (!feedStore.selectedCalendarId || !pendingActorId.value) {
    handleClose();
    return;
  }

  isSavingMappings.value = true;
  try {
    const feedService = new FeedService();
    const calendarId = feedStore.selectedCalendarId;

    // Resolve any "__add__" entries by creating new local categories first
    const resolvedMappings: CategoryMappingEntry[] = [];
    for (const mapping of pendingMappings.value) {
      if (mapping.localCategoryId === ADD_CATEGORY_VALUE) {
        // Create a new local category named after the source category
        const newCategory = await feedService.createLocalCategory(calendarId, mapping.sourceCategoryName);
        resolvedMappings.push({
          sourceCategoryId: mapping.sourceCategoryId,
          sourceCategoryName: mapping.sourceCategoryName,
          localCategoryId: newCategory.id,
        });
      }
      else {
        resolvedMappings.push(mapping);
      }
    }

    await feedService.setCategoryMappings(calendarId, pendingActorId.value, resolvedMappings);
  }
  catch (error) {
    console.error('Error saving category mappings:', error);
    // Non-fatal: close anyway so user isn't blocked
  }
  finally {
    isSavingMappings.value = false;
    handleClose();
  }
};

const handleClose = () => {
  // Reset form
  identifier.value = '';
  autoRepostOriginals.value = false;
  autoRepostReposts.value = false;
  preview.value = null;
  lookupError.value = '';
  isLookingUp.value = false;
  isFollowing.value = false;

  // Reset mapping step
  showMappingStep.value = false;
  pendingActorId.value = '';
  pendingSourceCategories.value = [];
  localCategories.value = [];
  pendingMappings.value = [];

  if (lookupTimeout) {
    clearTimeout(lookupTimeout);
    lookupTimeout = null;
  }

  emit('close');
};
</script>

<template>
  <Modal
    :title="showMappingStep ? t('add_calendar_modal.mapping_step_title') : t('add_calendar_modal.title')"
    @close="handleClose"
  >
    <!-- Follow step -->
    <div
      v-if="!showMappingStep"
      class="add-calendar-modal"
    >
      <div class="form-group">
        <label for="calendar-identifier">
          {{ t('add_calendar_modal.identifier_label') }}
        </label>
        <input
          id="calendar-identifier"
          v-model="identifier"
          type="text"
          :placeholder="t('add_calendar_modal.identifier_placeholder')"
          @input="handleIdentifierInput"
          class="identifier-input"
        />
        <p class="help-text">
          {{ t('add_calendar_modal.identifier_help') }}
        </p>
      </div>

      <div
        v-if="isLookingUp"
        class="loading-state"
        role="status"
        aria-live="polite"
      >
        <span class="loading-spinner" aria-hidden="true" />
        <span>{{ t('add_calendar_modal.looking_up') }}</span>
      </div>

      <div
        v-if="lookupError"
        class="error-state"
        role="alert"
      >
        {{ lookupError }}
      </div>

      <div v-if="preview" class="preview-section">
        <h3>{{ t('add_calendar_modal.preview_title') }}</h3>
        <div class="preview-content">
          <div class="preview-item">
            <strong>{{ t('add_calendar_modal.preview_name') }}:</strong> {{ preview.name }}
          </div>
          <div v-if="preview.description" class="preview-item">
            <strong>{{ t('add_calendar_modal.preview_description') }}:</strong> {{ preview.description }}
          </div>
          <div class="preview-item">
            <strong>{{ t('add_calendar_modal.preview_domain') }}:</strong> {{ preview.domain }}
          </div>
        </div>

        <div class="policy-section">
          <h4>{{ t('add_calendar_modal.policy_title') }}</h4>
          <p class="policy-description">{{ t('add_calendar_modal.policy_description') }}</p>

          <div class="policy-toggles">
            <ToggleSwitch
              id="auto-repost-originals"
              :model-value="autoRepostOriginals"
              :label="t('add_calendar_modal.auto_repost_originals')"
              :help-text="t('add_calendar_modal.auto_repost_originals_help')"
              @update:model-value="handleOriginalsToggle"
            />

            <ToggleSwitch
              v-if="autoRepostOriginals"
              id="auto-repost-reposts"
              :model-value="autoRepostReposts"
              :label="t('add_calendar_modal.auto_repost_reposts')"
              :help-text="t('add_calendar_modal.auto_repost_reposts_help')"
              @update:model-value="handleRepostsToggle"
            />
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button
          type="button"
          class="btn btn--secondary"
          @click="handleClose"
        >
          {{ t('add_calendar_modal.cancel_button') }}
        </button>
        <button
          type="button"
          class="btn btn--primary"
          :disabled="!canFollow"
          :aria-disabled="!canFollow"
          @click="handleFollow"
        >
          {{ isFollowing ? t('add_calendar_modal.following_button') : t('add_calendar_modal.follow_button') }}
        </button>
      </div>
    </div>

    <!-- Category mapping step -->
    <div
      v-else
      ref="mappingStepRef"
      tabindex="-1"
      class="add-calendar-modal mapping-step"
    >
      <p class="mapping-step-description">
        {{ t('add_calendar_modal.mapping_step_description') }}
      </p>

      <CategoryMappingEditor
        v-model="pendingMappings"
        :source-categories="pendingSourceCategories"
        :local-categories="localCategories"
      />

      <div class="modal-actions">
        <button
          type="button"
          class="btn btn--secondary"
          @click="handleClose"
        >
          {{ t('add_calendar_modal.mapping_skip_button') }}
        </button>
        <button
          type="button"
          class="btn btn--primary"
          :disabled="isSavingMappings"
          :aria-disabled="isSavingMappings"
          @click="saveMappingsAndClose"
        >
          {{ isSavingMappings ? t('add_calendar_modal.mapping_saving_button') : t('add_calendar_modal.mapping_save_button') }}
        </button>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
div.add-calendar-modal {
  display: flex;
  flex-direction: column;
  gap: var(--pav-space-4);
  min-width: 300px;

  @media (min-width: 768px) {
    min-width: 500px;
  }

  &.mapping-step {
    p.mapping-step-description {
      font-size: 0.875rem;
      color: rgba(0, 0, 0, 0.6);
      margin: 0;

      @media (prefers-color-scheme: dark) {
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }

  div.form-group {
    display: flex;
    flex-direction: column;
    gap: var(--pav-space-2);

    label {
      font-weight: var(--pav-font-weight-medium);
      color: var(--pav-color-text-primary);
    }

    input.identifier-input {
      padding: var(--pav-space-3);
      border-radius: var(--pav-border-radius-pill);
      border: 1px solid rgba(0, 0, 0, 0.2);
      background: white;
      color: var(--pav-color-text-primary);
      font-size: 1rem;

      @media (prefers-color-scheme: dark) {
        background: var(--pav-color-surface-primary);
        border-color: rgba(255, 255, 255, 0.2);
      }

      &:focus {
        outline: 2px solid var(--pav-color-interactive-primary);
        outline-offset: 2px;
      }
    }

    p.help-text {
      font-size: 0.875rem;
      color: rgba(0, 0, 0, 0.6);
      margin: 0;

      @media (prefers-color-scheme: dark) {
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }

  div.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--pav-space-3);
    padding: var(--pav-space-4);
    background: rgba(0, 0, 0, 0.05);
    border-radius: var(--pav-border-radius-pill);
    color: var(--pav-color-text-primary);

    @media (prefers-color-scheme: dark) {
      background: rgba(255, 255, 255, 0.05);
    }

    span.loading-spinner {
      display: inline-block;
      width: 20px;
      height: 20px;
      border: 3px solid rgba(0, 0, 0, 0.15);
      border-top-color: var(--pav-color-interactive-primary);
      border-radius: 50%;
      animation: spinner-rotate 0.8s linear infinite;
      flex-shrink: 0;

      @media (prefers-color-scheme: dark) {
        border-color: rgba(255, 255, 255, 0.15);
        border-top-color: var(--pav-color-interactive-primary);
      }
    }
  }

  @keyframes spinner-rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  div.error-state {
    padding: var(--pav-space-3);
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: var(--pav-border-radius-pill);
    color: rgb(239, 68, 68);
  }

  div.preview-section {
    padding: var(--pav-space-4);
    background: rgba(0, 0, 0, 0.05);
    border-radius: var(--pav-border-radius-pill);

    @media (prefers-color-scheme: dark) {
      background: rgba(255, 255, 255, 0.05);
    }

    h3 {
      margin: 0 0 var(--pav-space-3) 0;
      font-size: 1.125rem;
      font-weight: var(--pav-font-weight-medium);
      color: var(--pav-color-text-primary);
    }

    div.preview-content {
      display: flex;
      flex-direction: column;
      gap: var(--pav-space-2);
      margin-bottom: var(--pav-space-4);

      div.preview-item {
        font-size: 0.875rem;
        color: var(--pav-color-text-primary);

        strong {
          font-weight: var(--pav-font-weight-medium);
        }
      }
    }

    div.policy-section {
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      padding-top: var(--pav-space-4);

      @media (prefers-color-scheme: dark) {
        border-color: rgba(255, 255, 255, 0.1);
      }

      h4 {
        margin: 0 0 var(--pav-space-2) 0;
        font-size: 1rem;
        font-weight: var(--pav-font-weight-medium);
        color: var(--pav-color-text-primary);
      }

      p.policy-description {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.6);
        margin: 0 0 var(--pav-space-3) 0;

        @media (prefers-color-scheme: dark) {
          color: rgba(255, 255, 255, 0.6);
        }
      }

      div.policy-toggles {
        display: flex;
        flex-direction: column;
        gap: var(--pav-space-3);
      }
    }
  }

  div.modal-actions {
    display: flex;
    gap: var(--pav-space-3);
    justify-content: flex-end;
    margin-top: var(--pav-space-3);

    button {
      padding: var(--pav-space-2) var(--pav-space-4);
      border-radius: var(--pav-border-radius-pill);
      font-weight: var(--pav-font-weight-medium);
      cursor: pointer;
      transition: all 0.2s ease;

      &.secondary {
        background: transparent;
        border: 1px solid rgba(0, 0, 0, 0.2);
        color: var(--pav-color-text-primary);

        @media (prefers-color-scheme: dark) {
          border-color: rgba(255, 255, 255, 0.2);
        }

        &:hover {
          background: rgba(0, 0, 0, 0.05);

          @media (prefers-color-scheme: dark) {
            background: rgba(255, 255, 255, 0.05);
          }
        }
      }

      &.primary {
        background: var(--pav-color-interactive-primary);
        border: none;
        color: white;

        &:hover:not(:disabled) {
          background: var(--pav-color-interactive-primary-hover);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      &:focus {
        outline: 2px solid var(--pav-color-interactive-primary);
        outline-offset: 2px;
      }
    }
  }
}
</style>
